import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Grid, Typography } from '@mui/material';
import * as _ from 'lodash-es';

import { useSaveDraft } from '../../services/api/useSaveDraft';
import { useSpread } from '../../services/store/Spread';
import { usePostApplyToProduction } from '../../services/api/usePostApplyToProduction';
import { useTestPrint } from '../../services/api/useTestPrint';
import { useError } from '../../services/store/Error';
import { useConfirm } from '../../contexts/Confirm';

import { RESULT } from '../../types';
import { PAGE_PATH } from '../../constants';
import { translate } from '../../utils/translations';

export function Header(): React.JSX.Element {
  const { saveDraft } = useSaveDraft();
  const { spread, isDetail, getIsDirtyToSpread, setIsDirtyToSpread } =
    useSpread();
  const { postApplyToProduction } = usePostApplyToProduction();
  const { testPrint } = useTestPrint();
  const { setError } = useError();
  const confirm = useConfirm();
  const navigate = useNavigate();

  // 編集・挿入・削除された行からシートがdirtyかどうかを判別している
  const getIsDirty = () => {
    if (!spread) return false;

    for (let i = 0; i < spread.getSheetCount(); i++) {
      const sheet = spread.getSheet(i);

      if (
        sheet.getDirtyRows().length ||
        sheet.getInsertRows().length ||
        sheet.getDeletedRows().length
      ) {
        return true;
      }
    }

    return false;
  };

  // ワークブックのバリデーション
  const validateWorkbook = (): boolean => {
    if (!spread) return false;

    const noPrintAreaSheets: string[] = [];

    _.forEach(spread.sheets, (sheet) => {
      const printInfo = sheet.printInfo();

      // 印刷範囲設定があるかどうかを確認している
      if (
        _.some(
          [
            printInfo.rowStart(),
            printInfo.rowEnd(),
            printInfo.columnStart(),
            printInfo.columnEnd(),
          ],
          (num) => num === -1
        )
      ) {
        noPrintAreaSheets.push(sheet.name());
      }
    });

    if (noPrintAreaSheets.length > 0) {
      setError({
        title: translate('ERROR.PRINT_AREA_MISSING_TITLE'),
        message: translate('ERROR.PRINT_AREA_MISSING_MESSAGE', {
          sheets: noPrintAreaSheets.join('、'),
        }),
      });
      // ユーザーが印刷範囲の設定を行うことを見越して変更済みとする（印刷範囲の設定時にisDirtyを立てるという実装がないため）
      setIsDirtyToSpread(true);
      return false;
    }

    return true;
  };

  // 一時保存
  const handleClickSaveDraft = async () => {
    if (!spread) return;

    const ok = await confirm({
      message: translate('DIALOG_MESSAGES.SAVE_AS_DRAFT_MESSAGE'),
    });
    if (!ok) return;

    const result = await saveDraft();
    if (result.result !== RESULT.SUCCESS) return;

    // 編集状態を初期化する
    setIsDirtyToSpread(false);
    for (let i = 0; i < spread.getSheetCount(); i++) {
      const sheet = spread.getSheet(i);
      sheet.clearPendingChanges();
    }
  };

  // 本番登録
  const handleClickApply = async () => {
    if (!spread) return;
    let saveDraftResult;

    const validateResult = validateWorkbook();
    if (!validateResult) return;

    // 帳票が編集されていて一時保存が行われていない場合は一時保存を先に行う
    if (getIsDirty() || getIsDirtyToSpread()) {
      const ok = await confirm({
        message: translate('DIALOG_MESSAGES.SAVE_CHANGES_MESSAGE'),
      });
      if (!ok) return;

      // 一時保存
      const result = await saveDraft();
      if (result.result !== RESULT.SUCCESS) return;

      // 編集状態を初期化する
      setIsDirtyToSpread(false);
      for (let i = 0; i < spread.getSheetCount(); i++) {
        const sheet = spread.getSheet(i);
        sheet.clearPendingChanges();
      }
      saveDraftResult = result;
    }

    // 本番登録
    const ok = await confirm({
      message: translate('DIALOG_MESSAGES.REGISTER_FOR_PRODUCTION_MESSAGE'),
    });
    if (!ok) return;

    postApplyToProduction(saveDraftResult?.data?.templateRowVersion);
  };

  // 編集中止
  const handleCancelEdit = async () => {
    const ok = await confirm({
      message: translate('DIALOG_MESSAGES.FINISH_EDITTING_MESSAGE'),
    });
    if (!ok) return;

    navigate(PAGE_PATH.DETAIL);
  };

  // テスト出力
  const handleTestPrint = async () => {
    if (!spread) return;

    const validateResult = validateWorkbook();
    if (!validateResult) return;

    // 帳票が編集されていて一時保存が行われていない場合は一時保存を先に行う
    if (getIsDirty() || getIsDirtyToSpread()) {
      const ok = await confirm({
        message: translate('DIALOG_MESSAGES.SAVE_CHANGES_MESSAGE'),
      });
      if (!ok) return;

      // 一時保存
      const result = await saveDraft();
      if (result.result !== RESULT.SUCCESS) return;

      // 編集状態を初期化する
      setIsDirtyToSpread(false);
      for (let i = 0; i < spread.getSheetCount(); i++) {
        const sheet = spread.getSheet(i);
        sheet.clearPendingChanges();
      }
    }

    // テスト出力
    const ok = await confirm({
      message: translate('DIALOG_MESSAGES.EXECUTE_TEST_OUTPUT_MESSAGE'),
    });
    if (!ok) return;

    testPrint();
  };

  return (
    <Grid
      container
      alignItems="center"
      justifyContent="space-between"
      mb={2}
      px={3}
    >
      <Grid>
        <Typography variant="h1">{translate('REPORT_BUILDER')}</Typography>
      </Grid>
      <Grid>
        <Grid container spacing={1.5}>
          <Grid>
            <Button
              color="primary"
              onClick={handleTestPrint}
              variant="contained"
              sx={{ width: 100, height: 32, fontSize: 12 }}
              disabled={isDetail}
            >
              {translate('EXECUTE_TEST_OUTPUT')}
            </Button>
          </Grid>
          <Grid>
            <Button
              color="primary"
              onClick={handleClickSaveDraft}
              variant="contained"
              sx={{ width: 100, height: 32, fontSize: 12 }}
              disabled={isDetail}
            >
              {translate('SAVE_AS_DRAFT')}
            </Button>
          </Grid>
          <Grid>
            <Button
              color="warning"
              onClick={handleCancelEdit}
              variant="outlined"
              sx={{ width: 100, height: 32, fontSize: 12 }}
              disabled={isDetail}
            >
              {translate('CANCEL_EDIT')}
            </Button>
          </Grid>
          <Grid>
            <Button
              color="accent"
              onClick={handleClickApply}
              variant="contained"
              sx={{ width: 100, height: 32, fontSize: 12 }}
              disabled={isDetail}
            >
              {translate('REGISTER_FOR_PRODUCTION')}
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}
